import React, { useState, useEffect, useCallback, useRef } from 'react';
import { TextField, Button, Grid, FormHelperText, Typography, Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { QuattrLogoSpinner } from './components/QuattrLogoSpinner';
import upload from '../../assets/images/upload-file.png';
import CustomerDropDownWithLock from './components/CustomerDropDownWithLock';
import { fetchAIBasedContent } from '../../utility/cr-common';
import { AxiosError } from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import LockOpenIcon from '@material-ui/icons/Lock';
import {
  COMPONENT_NAMES,
  SCREEN_NAMES,
  CONTENT_AI_TOOL_NAME,
  CONTENT_AI_TOOL_CATEGORY,
  trackAnalyticsEvent,
} from '../../../src/constants/analyticsMeta';

import { CONTINUE_USING_TEXT } from './components/ToolsStyleWrapper';
import {
  getToolsUsageCreditsNeeded,
  handleGenerateOutput,
  PAID_TOOLS_PROMPT_FOR,
  resetCaptcha,
} from '../../utility/common';
import Cookies from 'js-cookie';
import { SkipCaptcha } from './components/SkipCaptcha';
import Dropzone, { useDropzone } from 'react-dropzone';
import { useSession } from '../../utility/services/customHooks';
import { SUBSCRIPTION_STATUS } from '../../constants/common';
import CreditsInfo from './components/CreditsInfo';
import imageCompression from 'browser-image-compression';

const useStyles = makeStyles((theme: Theme) => ({
  buttonGroup: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  dropZone: {
    width: '100%',
    height: '60px',
    marginBottom: '8px',
    background: 'rgba(33, 33, 33, 0.08)',
    border: '1px dashed rgba(0, 0, 0, 0.87)',
    boxSizing: 'border-box',
    borderRadius: '4px !important',
    textAlign: 'center',
    alignContent: 'center',
    cursor: 'pointer',
  },
  dropZoneText: {
    fontSize: '16px',
    textAlign: 'center',
    letterSpacing: '0.18px',
    color: 'rgba(0, 0, 0, 0.6)',
    margin: '0px',
    [theme.breakpoints.down(400)]: {
      fontSize: '11px',
    },
  },
  dropZoneTextShortened: {
    fontSize: '16px',
    textAlign: 'center',
    letterSpacing: '0.18px',
    color: 'rgba(0, 0, 0, 0.6)',
    margin: '0px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    maxWidth: '400px',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down(400)]: {
      fontSize: '11px',
      maxWidth: '230px',
    },
  },
}));

const ImageAltTextGenerator = ({
  classes,
  setShowSignUpPage,
  user,
  hasValidSubscription,
  resetLockedGrace,
  enableLockedOption,
  handleResetUsage,
  isQwaDashboard = false,
  isCreditsShown = false,
  creditsRemaining = 0,
  usagePerDay = 0,
  setShowBuyOnePass,
  setIsRedirectToWebflow,
  setShowMinimumCreditsNeededMsg = (a) => {},
  setShowVerifyEmailPopup,
  freeToolsUsage = 0,
  showFreeToolsUsage = false,
  isCorpEmail = false,
}) => {
  const ownClasses = useStyles();
  const qttr_tools_auth = Cookies.get('qttr_tools_auth');
  const qttr_tools_daypass_auth = Cookies.get('qttr_tools_daypass_auth');
  const [keyword, setKeyword] = useState('');
  const [description, setDescription] = useState('');
  const [numTags, setNumTags] = useState(3);
  const [generatedTitles, setGeneratedTitles] = useState('');
  const [imageBase64, setImageBase64] = useState('');
  const [fileData, setFileData] = useState<any>(null);
  const [dropBoxError, setDropBoxError] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const [captchaError, setCaptchaError] = useState('');
  const [creditsNeeded, setCreditsNeeded] = useState(1);
  const inputSelectionRef = useRef<HTMLInputElement>(null);
  const session = useSession();

  const handleRecaptchaChange = (value) => {
    setRecaptchaValue(value);
    setCaptchaError('');
  };

  useEffect(() => {
    trackAnalyticsEvent({
      isAnonymousUser: !user,
      payload: {
        label: 'Visited Free Tool',
        component_name: COMPONENT_NAMES.contentai_ai_tools,
        screen_name: SCREEN_NAMES.contentai_ai_tools,
        tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
        tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
        isQwaDashboard: isQwaDashboard,
      },
    });
  }, []);

  const handleWriteTitleTag = async () => {
    try {
      setLoading(true);
      if (!fileData && !keyword) {
        setError('keyword');
        return;
      }
      if (!(recaptchaValue || qttr_tools_auth || user)) {
        setCaptchaError('recaptchaValue');
        return;
      }

      setError('');
      setCaptchaError('');

      const res = await fetchAIBasedContent({
        promptFor: 'PUBLIC_UTILITY.GENERATE_ALT_TEXT',
        promptConfig: {
          noOf: numTags,
          content: description,
          specialInstructions: description,
          mainKeyword: keyword,
          image_link: imageBase64,
        },
        tokenLimit: 3500,
        isPublicApi: true,
        reCAPTCHAToken: recaptchaValue,
      });
      if (res) {
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: `${CONTENT_AI_TOOL_CATEGORY.ai_content_optimization} Outputs`,
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Generate Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
            number_suggestions: numTags,
            special_instructions_yn: description ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            isImageAdded: imageBase64 ? true : false,
            creditsConsumed: creditsNeeded ? creditsNeeded : 1,
          },
        });
        if (handleResetUsage) {
          handleResetUsage();
        }
      }
      setGeneratedTitles(res || 'no title generated');
      if (resetLockedGrace) {
        resetLockedGrace();
        setNumTags(3);
      }
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError?.response) {
        if (axiosError.response.status === 403) {
          setShowSignUpPage(CONTINUE_USING_TEXT, {
            tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
          });
          trackAnalyticsEvent({
            eventFor: 'button',
            isAnonymousUser: !user,
            payload: {
              label: 'Reached your maximum usage limit',
              component_name: COMPONENT_NAMES.contentai_ai_tools,
              screen_name: SCREEN_NAMES.contentai_ai_tools,
              tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
              tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
              isQwaDashboard: isQwaDashboard,
              isImageAdded: imageBase64 ? true : false,
            },
          });
        } else if (axiosError?.response.status === 500) {
          setDropBoxError('Failed to process the image. Please try again.');
          trackAnalyticsEvent({
            eventFor: 'button',
            isAnonymousUser: !user,
            payload: {
              label: 'Failed to process the image. Please try again.',
              component_name: COMPONENT_NAMES.contentai_ai_tools,
              screen_name: SCREEN_NAMES.contentai_ai_tools,
              tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
              tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
              isQwaDashboard: isQwaDashboard,
              isImageAdded: imageBase64 ? true : false,
            },
          });
        }
      }
      console.error('handleWriteTitleTag::error', error);
    } finally {
      setRecaptchaValue(null);
      resetCaptcha();
      setLoading(false);
    }
  };

  const handleCopyText = () => {
    const inputElement = inputSelectionRef?.current as any;
    if (inputElement) {
      const copiedText = inputElement?.value?.substring(
        inputElement?.selectionStart || 0,
        inputElement?.selectionEnd || 0
      );
      if (copiedText) {
        navigator.clipboard.writeText(copiedText);
        setCopied(true);
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: 'Outputs Accepted',
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Copy Generated Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
            number_suggestions: numTags,
            special_instructions_yn: description ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            isImageAdded: imageBase64 ? true : false,
            isManualCopy: true,
          },
        });
      } else {
        navigator.clipboard.writeText(generatedTitles);
        setCopied(true);
        trackAnalyticsEvent({
          eventFor: 'button',
          buttonText: '',
          counterName: 'Outputs Accepted',
          isAddCount: true,
          user,
          isAnonymousUser: !user,
          payload: {
            label: 'Copy Generated Output',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
            number_suggestions: numTags,
            special_instructions_yn: description ? 'Yes' : 'No',
            isQwaDashboard: isQwaDashboard,
            isImageAdded: imageBase64 ? true : false,
          },
        });
      }
    }

    setTimeout(() => {
      setCopied(false);
    }, 2000); // Change the text back to 'Copy Text' after 2 seconds
  };

  const numbers = [3, 5, 10];

  // Convert the array items to a mixed array of a number and objects
  const NumberOptions = numbers.map((number) => {
    // Keep the first item as a number
    if ([3].includes(number)) {
      return number;
    }

    // Convert the rest to objects, include a React component for the icon
    return {
      name: number,
      requireLogin: !hasValidSubscription && !enableLockedOption,
      icon:
        !hasValidSubscription && !enableLockedOption ? (
          <LockOpenIcon className={classes.unlockIcon} />
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none">
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M9 12.9525L13.635 15.75L12.405 10.4775L16.5 6.93L11.1075 6.4725L9 1.5L6.8925 6.4725L1.5 6.93L5.595 10.4775L4.365 15.75L9 12.9525Z"
              fill="#FFC72C"
            />
          </svg>
        ), // This is a JSX element representing the icon
    };
  });

  const onDrop = useCallback(
    async (acceptedFiles, fileRejections) => {
      // Handle invalid subscription or locked option
      if (!hasValidSubscription && !enableLockedOption) {
        setShowSignUpPage(`'enable {value} Alt Text Suggestions'`, {
          tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
          tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
        });
        return;
      }
      setDropBoxError('');

      // Display error for rejected files
      fileRejections.forEach(({ errors }) =>
        errors.forEach(({ code }) => {
          if (code === 'file-invalid-type') {
            setFileData('');
            setImageBase64('');
            setDropBoxError('This image format is not supported. Please upload the image in supported format.');
          } else if (code === 'file-too-large') {
            setFileData('');
            setImageBase64('');
            setDropBoxError('The image size exceeds 8MB. Please upload a smaller file to proceed.');
          }
        })
      );

      const file = acceptedFiles[0];
      if (!file) return;

      const fileSizeMB = file.size / 1024 / 1024;

      try {
        let base64;

        // Compress file only if size > 5.6MB
        if (fileSizeMB > 5.6) {
          do {
            const newFile = await imageCompression(file, {
              maxSizeMB: file.size / 1024 / 1024 - 1, // Gradually reduce file size
              maxWidthOrHeight: 1024, // Resize dimensions
              useWebWorker: true, // Enable Web Worker for performance
            });
            base64 = await imageCompression.getDataUrlFromFile(newFile);
          } while ((base64.length * 3) / 4 / (1024 * 1024) > 7); // Ensure Base64 is under 7MB
        } else {
          // Convert smaller files directly to Base64
          base64 = await imageCompression.getDataUrlFromFile(file);
        }

        // Update file and Base64 data
        setFileData(file);
        setImageBase64(base64);

        trackAnalyticsEvent({
          eventName: 'Uploaded Image file',
          eventFor: 'btn',
          payload: {
            label: 'Uploaded Image file',
            component_name: COMPONENT_NAMES.contentai_ai_tools,
            screen_name: SCREEN_NAMES.contentai_ai_tools,
            tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
          },
        });
      } catch (error) {
        console.error('Image compression error:', error);
        setDropBoxError('Failed to process the image. Please try again.');
      }
    },
    [hasValidSubscription, enableLockedOption, setShowSignUpPage]
  );

  useEffect(() => {
    const promptFor = PAID_TOOLS_PROMPT_FOR.IMAGE_ALT_TEXT_GENERATOR;
    const promptConfig = {
      noOf: numTags,
      content: description,
      specialInstructions: description,
      mainKeyword: keyword,
      image_link: imageBase64,
    };
    setCreditsNeeded(getToolsUsageCreditsNeeded(promptFor, promptConfig));
  }, [user, description, keyword, numTags, imageBase64]);

  return (
    <>
      {generatedTitles ? (
        <div className={classes.container}>
          <TextField
            inputRef={inputSelectionRef}
            fullWidth
            multiline
            rows={8}
            margin="normal"
            placeholder="Generated Title Tag"
            value={generatedTitles}
            className={`${classes.textAreaInput} no-border background-gray`}
            maxRows={8}
            InputProps={{
              style: { borderRadius: '4px !important', padding: '16px' },
            }}
            minRows={5}
            variant="outlined"
            onCopy={handleCopyText}
          />
          <div className={classes.buttonGroup} style={{ marginTop: '16px' }}>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.toolsOutlinedCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={() => {
                setGeneratedTitles('');
                trackAnalyticsEvent({
                  eventName: 'Clicked on Go back',
                  payload: {
                    label: 'Clicked on Go back',
                  },
                });
              }}
            >
              Go back
            </Button>
            <Button
              className={`${classes.toolsYellowCTA} ${classes.button}`}
              disabled={loading}
              style={{ float: 'right' }}
              onClick={handleCopyText}
            >
              {copied ? 'Copied!' : 'Copy Text'}
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className={classes.container}>
            {loading && <QuattrLogoSpinner classes={classes} />}
            <TextField
              fullWidth
              label="Enter Topic or Main Keyword"
              value={keyword}
              className={`${classes.textAreaInput} no-border mb-0`}
              onChange={(e) => setKeyword(e.target.value)}
              InputProps={{
                style: { backgroundColor: '#fff', borderRadius: '4px !important' },
              }}
              variant="outlined"
              error={!!error}
              helperText={error.includes('keyword') ? '* Required' : error.includes('Please sign up') ? error : ''}
            />

            <TextField
              fullWidth
              placeholder={`Describe image contents\nEx. An image collage of top Christmas sweater styles`}
              multiline
              className={classes.textAreaInput}
              value={description}
              maxRows={5}
              InputProps={{
                style: { backgroundColor: '#fff', borderRadius: '4px !important', padding: '16px' },
                disableUnderline: true,
              }}
              minRows={5}
              variant="filled"
              onChange={(e) => setDescription(e.target.value)}
            />
            <Typography className={classes.orText}>OR</Typography>

            <Dropzone onDrop={onDrop} accept=".png,.jpg,.jpeg,.webp" maxSize={8388600}>
              {({ getRootProps, getInputProps, isDragActive }) => (
                <>
                  <div className={ownClasses.dropZone} {...getRootProps()}>
                    <input {...getInputProps()} disabled={!hasValidSubscription && !enableLockedOption} />
                    <div
                      onClick={() => {
                        if (!hasValidSubscription && !enableLockedOption) {
                          setShowSignUpPage(`'enable {value} Alt Text Suggestions'`, {
                            tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
                            tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
                          });
                        }
                      }}
                      style={{ display: 'flex', justifyContent: 'center' }}
                    >
                      <img
                        height={15}
                        width={15}
                        src={upload.src}
                        style={{ marginRight: '15px', marginLeft: '10px', alignSelf: 'center' }}
                      />
                      {fileData && fileData?.path ? (
                        <p className={ownClasses.dropZoneTextShortened}>{fileData.path}</p>
                      ) : (
                        <p className={ownClasses.dropZoneText}>Drag and drop your image or click to upload</p>
                      )}

                      {!hasValidSubscription && !enableLockedOption ? (
                        <LockOpenIcon className={classes.unlockIcon} style={{ marginLeft: '10px' }} />
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ marginLeft: '10px' }}
                          width="18"
                          height="18"
                          viewBox="0 0 18 18"
                          fill="none"
                        >
                          <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M9 12.9525L13.635 15.75L12.405 10.4775L16.5 6.93L11.1075 6.4725L9 1.5L6.8925 6.4725L1.5 6.93L5.595 10.4775L4.365 15.75L9 12.9525Z"
                            fill="#FFC72C"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="pd-15 mb-10">
                    {dropBoxError ? <p className="negative-text">{dropBoxError}</p> : null}
                  </div>
                  <div className="pd-15 mb-10">
                    {
                      <p
                        style={{
                          margin: '0px 0px 24px 0px',
                          color: 'rgba(0, 0, 0, 0.6)',
                          fontWeight: '500',
                          fontSize: '12px',
                        }}
                      >
                        Supported File Format: PNG, JPG, JPEG, WEBP; &nbsp; Maximum File Size: 8MB{' '}
                      </p>
                    }
                  </div>
                </>
              )}
            </Dropzone>

            <CustomerDropDownWithLock
              label="Number of Alt Text Suggestions"
              value={numTags}
              options={NumberOptions}
              onChange={(event: React.ChangeEvent<{ value: unknown }>) => {
                const value = event.target.value as number;
                const option = NumberOptions.find((o) => {
                  return typeof o === 'object' && 'name' in o ? o.name === value : o === value;
                });
                if (option && (typeof option === 'number' || !(typeof option == 'object' && option.requireLogin))) {
                  setNumTags(value);
                } else {
                  if (setShowSignUpPage) {
                    setShowSignUpPage(`'enable ${value} Alt Text Suggestions'`, {
                      tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
                      tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
                    });
                    trackAnalyticsEvent({
                      eventFor: 'button',
                      isAnonymousUser: !user,
                      payload: {
                        label: 'Clicked Locked Option',
                        component_name: COMPONENT_NAMES.contentai_ai_tools,
                        screen_name: SCREEN_NAMES.contentai_ai_tools,
                        tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
                        tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
                        option_name: `${value} Alt Text Suggestion`,
                        isQwaDashboard: isQwaDashboard,
                      },
                    });
                  }
                }
                // setError('');
              }}
              classes={classes}
            />
            {!qttr_tools_auth && !user && (
              <>
                <ReCAPTCHA
                  className={classes.recaptcha}
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY}
                  onChange={handleRecaptchaChange}
                />
                {captchaError?.includes('recaptchaValue') && (
                  <FormHelperText className="red">Please complete the reCAPTCHA to verify you're human.</FormHelperText>
                )}
                <SkipCaptcha
                  classes={classes}
                  meta={{
                    component_name: COMPONENT_NAMES.contentai_ai_tools,
                    screen_name: SCREEN_NAMES.contentai_ai_tools,
                    tool_name: CONTENT_AI_TOOL_NAME.image_alt_text_generator,
                    tool_category: CONTENT_AI_TOOL_CATEGORY.ai_content_optimization,
                  }}
                  setShowVerifyEmailPopup={setShowVerifyEmailPopup}
                />
              </>
            )}
            <div className={`${isCreditsShown ? classes.creditsShown : classes.freeCreditsShown}`}>
              {isCreditsShown ? (
                <CreditsInfo
                  classes={classes}
                  creditsRemaining={creditsRemaining}
                  creditsNeeded={creditsNeeded}
                  usagePerDay={usagePerDay}
                />
              ) : !isQwaDashboard || showFreeToolsUsage ? (
                <CreditsInfo
                  classes={classes}
                  creditsRemaining={freeToolsUsage}
                  creditsNeeded={creditsNeeded}
                  usagePerDay={isCorpEmail ? 10 : 7}
                  showFreeToolsUsage={showFreeToolsUsage}
                />
              ) : null}
              <div className={`d-flex ${classes.buttonGroup}`} style={{ marginTop: '16px' }}>
                <Button
                  style={{ float: 'right' }}
                  className={`${classes.toolsYellowCTA} ${classes.toolsOutlinedCTA} ${classes.button}`}
                  onClick={() => {
                    setKeyword('');
                    setDescription('');
                    setGeneratedTitles('');
                    setNumTags(3);
                    setImageBase64('');
                    setFileData('');
                    setDropBoxError('');
                    setError('');
                  }}
                >
                  Clear
                </Button>
                <Button
                  className={`${classes.toolsYellowCTA} ${classes.button}`}
                  color="primary"
                  onClick={() =>
                    handleGenerateOutput(
                      creditsRemaining,
                      creditsNeeded,
                      isQwaDashboard,
                      session,
                      qttr_tools_daypass_auth,
                      setShowMinimumCreditsNeededMsg,
                      setShowBuyOnePass,
                      handleWriteTitleTag,
                      trackAnalyticsEvent,
                      freeToolsUsage,
                      showFreeToolsUsage
                    )
                  }
                >
                  Generate Alt Text
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ImageAltTextGenerator;
